import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Lenis from 'lenis';
import { AuthProvider, useAuth } from './hooks/useAuth';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import LandingPage from './components/LandingPage';
import ImageGenerator from './components/ImageGenerator';
import VideoGenerator from './components/VideoGenerator';
import ModelSelector from './components/ModelSelector';
import Gallery from './components/Gallery';
import UserProfile from './components/UserProfile';
import Credits from './components/Credits';
import Navbar from './components/Navbar';
import { motion } from 'framer-motion';

function LenisProvider({ children }) {
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    return () => lenis.destroy();
  }, []);

  return children;
}

function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#000' }}>
        <motion.div
          animate={{ opacity: [0.3, 1, 0.3] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
          className="flex flex-col items-center gap-4"
        >
          <div
            className="w-10 h-10 rounded-2xl"
            style={{
              background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.14)',
              backdropFilter: 'blur(12px)',
            }}
          />
          <span
            style={{ fontFamily: 'var(--font-body)', color: 'rgba(255,255,255,0.4)', fontSize: 13, letterSpacing: '0.12em' }}
            className="uppercase font-medium"
          >
            Caricamento
          </span>
        </motion.div>
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" replace />;
}

function AuthenticatedLayout({ children }) {
  return (
    <div className="min-h-screen" style={{ background: '#000' }}>
      {/* subtle background glow */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div style={{
          position: 'absolute',
          width: '600px', height: '400px',
          top: '-100px', right: '-100px',
          background: 'radial-gradient(ellipse, rgba(35,136,255,0.07) 0%, transparent 70%)',
          filter: 'blur(80px)',
        }} />
        <div style={{
          position: 'absolute',
          width: '500px', height: '400px',
          bottom: '0', left: '-100px',
          background: 'radial-gradient(ellipse, rgba(74,58,255,0.05) 0%, transparent 70%)',
          filter: 'blur(80px)',
        }} />
      </div>
      <div className="relative z-10">
        <Navbar />
        <main className="container mx-auto px-4 py-4 md:py-8">
          {children}
        </main>
      </div>
    </div>
  );
}

function AppRoutes() {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />}
      />
      <Route
        path="/credits"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <Credits />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <Dashboard />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <UserProfile />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/image-generator"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <ImageGenerator />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/video-generator"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <VideoGenerator />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/models"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <ModelSelector />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/gallery"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <Gallery />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <LenisProvider>
        <AppRoutes />
      </LenisProvider>
    </AuthProvider>
  );
}

export default App;
